// src/pages/VerifyInvitationPage.jsx
import React, { useState, useEffect, useRef } from 'react';
import { useSearchParams, useNavigate, Link } from 'react-router-dom';
import { Mail, Lock, AlertCircle, Loader2, CheckCircle2, Eye, EyeOff, UserCircle, Building2 } from 'lucide-react';
import { auth } from '../firebase';
import { fetchSignInMethodsForEmail } from 'firebase/auth';
import { useAuth } from '../contexts/AuthContext';
import dvsLogo from '../assets/branding/dva_logo.png';

const SESSIONS_API_URL = import.meta.env.VITE_SESSIONS_API_URL || 'https://dva-admin-backend-dev-813595575009.us-central1.run.app';
const PUBLIC_API_KEY = import.meta.env.VITE_PUBLIC_API_KEY;

export default function VerifyInvitationPage() {
  const [searchParams] = useSearchParams();
  const navigate = useNavigate();
  const { login, validateToken, logout } = useAuth();

  // Extract invitation token from URL
  const invitationToken = searchParams.get('token');

  // Ref to prevent duplicate API calls (React StrictMode issue)
  // Store the processed token value, not just a boolean
  const processedToken = useRef(null);

  // Invitation data
  const [invitationData, setInvitationData] = useState(null);
  const [email, setEmail] = useState('');
  const [pendingAccountToken, setPendingAccountToken] = useState(null);

  // Form state
  const [password, setPassword] = useState('');
  const [confirmPassword, setConfirmPassword] = useState('');
  const [showPassword, setShowPassword] = useState(false);
  const [showConfirmPassword, setShowConfirmPassword] = useState(false);

  // UI state
  const [loading, setLoading] = useState(true);
  const [validating, setValidating] = useState(false);
  const [accepting, setAccepting] = useState(false);
  const [submitting, setSubmitting] = useState(false);
  const [loggingIn, setLoggingIn] = useState(false);
  const [error, setError] = useState('');
  const [validationState, setValidationState] = useState('checking'); // checking, valid, invalid, accepted, password-exists
  const [needsPassword, setNeedsPassword] = useState(false);
  const [verificationEmailSent, setVerificationEmailSent] = useState(false);

  // Validate and accept invitation on component mount
  useEffect(() => {
    const processInvitation = async () => {
      // Prevent duplicate calls (React StrictMode runs effects twice in dev)
      // Check if we've already processed this specific token
      if (processedToken.current === invitationToken) {
        console.log('⏭️ Skipping duplicate invitation processing for token:', invitationToken);
        return;
      }

      if (!invitationToken) {
        setError('No invitation token provided in the URL');
        setValidationState('invalid');
        setLoading(false);
        return;
      }

      // Mark this token as being processed to prevent duplicate calls
      processedToken.current = invitationToken;

      try {
        setLoading(true);
        setValidating(true);

        console.log('🔍 Validating invitation:', invitationToken);

        // Step 1: Validate the invitation
        const validateResponse = await fetch(
          `${SESSIONS_API_URL}/public/invitations/${invitationToken}/validate`,
          {
            method: 'GET',
            headers: {
              'Content-Type': 'application/json',
              'X-API-Key': PUBLIC_API_KEY,
            },
          }
        );

        console.log('📊 Validate response status:', validateResponse.status);

        if (!validateResponse.ok) {
          const errorData = await validateResponse.json();
          console.error('❌ Validation failed:', errorData);
          throw new Error(errorData.message || errorData.error || 'Invalid or expired invitation');
        }

        const validateData = await validateResponse.json();
        console.log('✅ Invitation validated:', validateData);

        // Extract pending_account_token for later use (from data object)
        const token = validateData.data?.pending_account_token || validateData.pending_account_token;
        if (token) {
          setPendingAccountToken(token);
          console.log('🔑 Pending account token stored:', token);
        } else {
          console.warn('⚠️ No pending_account_token found in validate response');
        }

        // Store invitation data from validation response
        const userData = validateData.data || validateData;
        setInvitationData(userData);

        // Extract email from the response
        const userEmail = userData.email || userData.user?.email || '';
        setEmail(userEmail);

        // Check if password is already set
        if (userEmail) {
          console.log('🔐 Checking if password exists for:', userEmail);
          try {
            const signInMethods = await fetchSignInMethodsForEmail(auth, userEmail);
            console.log('📋 Sign-in methods found:', signInMethods);

            if (signInMethods.includes('password')) {
              console.log('✅ Password already set');
              setNeedsPassword(false);
              setValidationState('password-exists');
            } else {
              console.log('🔑 Password needs to be set');
              setNeedsPassword(true);
              setValidationState('valid'); // Changed from 'accepted' to 'valid'
            }
          } catch (firebaseErr) {
            console.error('⚠️ Firebase check failed:', firebaseErr);
            // Assume password needs to be set if we can't check
            setNeedsPassword(true);
            setValidationState('valid'); // Changed from 'accepted' to 'valid'
          }
        }

        setValidating(false);

      } catch (err) {
        console.error('❌ Invitation process error:', err);
        setError(err.message || 'Failed to process invitation. Please try again or contact support.');
        setValidationState('invalid');
      } finally {
        setLoading(false);
        setValidating(false);
      }
    };

    processInvitation();

    // No cleanup needed - processedToken.current persists to prevent duplicate calls
    // It will only process again if invitationToken changes to a different value
  }, [invitationToken]);

  // Password validation
  const validatePassword = (pwd) => {
    if (pwd.length < 8) {
      return 'Password must be at least 8 characters long';
    }
    if (!/[A-Z]/.test(pwd)) {
      return 'Password must contain at least one uppercase letter';
    }
    if (!/[a-z]/.test(pwd)) {
      return 'Password must contain at least one lowercase letter';
    }
    if (!/[0-9]/.test(pwd)) {
      return 'Password must contain at least one number';
    }
    return null;
  };

  // Handle password form submission
  const handleSubmit = async (e) => {
    e.preventDefault();
    setError('');

    // Validate password
    const passwordError = validatePassword(password);
    if (passwordError) {
      setError(passwordError);
      return;
    }

    // Check if passwords match
    if (password !== confirmPassword) {
      setError('Passwords do not match');
      return;
    }

    try {
      setSubmitting(true);

      console.log('🔐 Setting password for email:', email);

      // Use pending_account_token from validation response
      const tokenToUse = pendingAccountToken || invitationToken;
      console.log('🔑 Using token for set-password:', pendingAccountToken ? 'pending_account_token' : 'invitation token (fallback)');

      // Call the backend API to set password
      const response = await fetch(`${SESSIONS_API_URL}/auth/set-password`, {
        method: 'POST',
        headers: {
          'Content-Type': 'application/json',
          'X-API-Key': PUBLIC_API_KEY,
        },
        body: JSON.stringify({ token: tokenToUse, password }),
      });

      console.log('📊 Response status:', response.status);

      if (!response.ok) {
        const errorData = await response.json();
        console.error('❌ API error response:', errorData);
        throw new Error(errorData.message || errorData.error || 'Failed to set password');
      }

      const data = await response.json();
      console.log('✅ Password set successfully:', data);

      // Step 2: Accept the invitation after password is set
      console.log('📝 Accepting invitation...');
      const acceptResponse = await fetch(
        `${SESSIONS_API_URL}/public/invitations/${invitationToken}/accept`,
        {
          method: 'POST',
          headers: {
            'Content-Type': 'application/json',
            'X-API-Key': PUBLIC_API_KEY,
          },
        }
      );

      console.log('📊 Accept response status:', acceptResponse.status);

      if (!acceptResponse.ok) {
        const errorData = await acceptResponse.json();
        console.error('❌ Accept failed:', errorData);
        throw new Error(errorData.message || errorData.error || 'Failed to accept invitation');
      }

      const acceptData = await acceptResponse.json();
      console.log('✅ Invitation accepted:', acceptData);

      // Show logging in state
      setSubmitting(false);
      setLoggingIn(true);

      // Wait for backend to complete session setup before auto-login (6 seconds)
      console.log('⏳ Waiting for backend session setup...');
      await new Promise(resolve => setTimeout(resolve, 6000));

      // Auto-login the user
      try {
        // Step 1: Firebase Authentication
        console.log('🔐 Logging in user...');
        const user = await login(email, password);
        console.log('✅ Firebase auth successful:', user.uid);

        // Step 2: Get JWT token
        const token = await user.getIdToken();
        console.log('✅ JWT token obtained');

        // Step 3: Validate token with backend (creates session document)
        try {
          await validateToken(token, user.uid);
          console.log('✅ Session validated and subscribed');

          // Step 4: Navigate to home on success
          console.log('🏠 Redirecting to /home');
          navigate('/home');
        } catch (sessionError) {
          console.error('❌ Session validation failed:', sessionError);

          // Block access if session validation fails
          setLoggingIn(false);
          setError(sessionError.message || 'Session validation failed. Please try logging in manually.');

          // Sign out user since session validation failed
          await logout();
        }
      } catch (loginErr) {
        console.error('❌ Auto-login failed:', loginErr);
        // Password was set successfully but login failed
        // Redirect to login page with a message
        setLoggingIn(false);
        setError('Password set successfully! Please log in with your new password.');
        setTimeout(() => {
          navigate('/login');
        }, 2000);
      }
    } catch (err) {
      console.error('❌ Set password error:', err);
      setError(err.message || 'Failed to set password. Please try again or contact support.');
    } finally {
      setSubmitting(false);
      setLoggingIn(false);
    }
  };

  // Loading screen (validating)
  if (loading || validating) {
    return (
      <div className="min-h-screen bg-gradient-to-br from-[#FAFAFA] via-white to-[#18365E]/5 flex items-center justify-center px-4 py-8">
        <div className="w-full max-w-md">
          <div className="text-center mb-8">
            <div className="inline-flex items-center justify-center mb-6">
              <img src={dvsLogo} alt="DVA Logo" className="h-24 w-auto" />
            </div>
          </div>

          <div className="bg-white rounded-2xl shadow-xl border border-gray-100 p-8">
            <div className="text-center">
              <Loader2 className="w-12 h-12 text-[#18365E] animate-spin mx-auto mb-4" />
              <h2
                className="text-xl mb-2"
                style={{ fontWeight: 700, color: '#18365E', fontFamily: 'Montserrat, sans-serif' }}
              >
                Verifying Invitation...
              </h2>
              <p className="text-[#717182]" style={{ fontWeight: 500 }}>
                Please wait while we verify your invitation
              </p>
            </div>
          </div>
        </div>
      </div>
    );
  }

  // Logging in screen
  if (loggingIn) {
    return (
      <div className="min-h-screen bg-gradient-to-br from-[#FAFAFA] via-white to-[#18365E]/5 flex items-center justify-center px-4 py-8">
        <div className="w-full max-w-md">
          <div className="text-center mb-8">
            <div className="inline-flex items-center justify-center mb-6">
              <img src={dvsLogo} alt="DVA Logo" className="h-24 w-auto" />
            </div>
          </div>

          <div className="bg-white rounded-2xl shadow-xl border border-gray-100 p-8">
            <div className="text-center">
              <Loader2 className="w-12 h-12 text-[#18365E] animate-spin mx-auto mb-4" />
              <h2
                className="text-xl mb-2"
                style={{ fontWeight: 700, color: '#18365E', fontFamily: 'Montserrat, sans-serif' }}
              >
                Setting Up Your Account...
              </h2>
              <p className="text-[#717182]" style={{ fontWeight: 500 }}>
                Please wait while we complete your account setup and log you in
              </p>
            </div>
          </div>
        </div>
      </div>
    );
  }

  // Invalid invitation screen
  if (validationState === 'invalid') {
    return (
      <div className="min-h-screen bg-gradient-to-br from-[#FAFAFA] via-white to-[#18365E]/5 flex items-center justify-center px-4 py-8">
        <div className="w-full max-w-md">
          <div className="text-center mb-8">
            <div className="inline-flex items-center justify-center mb-6">
              <img src={dvsLogo} alt="DVA Logo" className="h-24 w-auto" />
            </div>
          </div>

          <div className="bg-white rounded-2xl shadow-xl border border-gray-100 p-8">
            <div className="text-center">
              <div className="inline-flex items-center justify-center w-16 h-16 bg-red-100 rounded-full mb-6">
                <AlertCircle className="w-8 h-8 text-red-600" />
              </div>

              <h2
                className="text-2xl mb-3"
                style={{ fontWeight: 700, color: '#18365E', fontFamily: 'Montserrat, sans-serif' }}
              >
                Invalid Invitation
              </h2>

              <p className="text-[#717182] mb-6" style={{ fontWeight: 500 }}>
                {error}
              </p>

              <div className="space-y-3">
                <Link
                  to="/signup"
                  className="block w-full px-6 py-3 bg-gradient-to-r from-[#18365E] to-[#18365E]/90 text-white rounded-lg hover:shadow-lg transition-all text-center"
                  style={{ fontWeight: 600 }}
                >
                  Sign Up
                </Link>

                <Link
                  to="/login"
                  className="block w-full px-6 py-3 border-2 border-[#18365E] text-[#18365E] rounded-lg hover:bg-[#18365E]/5 transition-all text-center"
                  style={{ fontWeight: 600 }}
                >
                  Go to Login
                </Link>
              </div>
            </div>
          </div>
        </div>
      </div>
    );
  }

  // Password already set - show welcome and redirect to login
  if (validationState === 'password-exists' && invitationData) {
    return (
      <div className="min-h-screen bg-gradient-to-br from-[#FAFAFA] via-white to-[#18365E]/5 flex items-center justify-center px-4 py-8">
        <div className="w-full max-w-md">
          <div className="text-center mb-8">
            <div className="inline-flex items-center justify-center mb-6">
              <img src={dvsLogo} alt="DVA Logo" className="h-24 w-auto" />
            </div>
          </div>

          <div className="bg-white rounded-2xl shadow-xl border border-gray-100 p-8">
            <div className="text-center">
              <div className="inline-flex items-center justify-center w-16 h-16 bg-green-100 rounded-full mb-6">
                <CheckCircle2 className="w-8 h-8 text-green-600" />
              </div>

              <h2
                className="text-2xl mb-3"
                style={{ fontWeight: 700, color: '#18365E', fontFamily: 'Montserrat, sans-serif' }}
              >
                Welcome to DVA!
              </h2>

              <p className="text-[#717182] mb-6" style={{ fontWeight: 500 }}>
                Your invitation has been accepted successfully.
              </p>

              {/* User Details */}
              <div className="bg-gray-50 rounded-lg p-4 mb-6 text-left">
                <div className="space-y-3">
                  <div className="flex items-start gap-3">
                    <Building2 className="w-5 h-5 text-[#18365E] mt-0.5" />
                    <div>
                      <p className="text-xs text-[#717182]" style={{ fontWeight: 500 }}>
                        Organization
                      </p>
                      <p className="text-sm text-[#18365E]" style={{ fontWeight: 600 }}>
                        {invitationData.client_name || invitationData.client?.name || 'N/A'}
                      </p>
                    </div>
                  </div>
                  <div className="flex items-start gap-3">
                    <UserCircle className="w-5 h-5 text-[#18365E] mt-0.5" />
                    <div>
                      <p className="text-xs text-[#717182]" style={{ fontWeight: 500 }}>
                        Your Account
                      </p>
                      <p className="text-sm text-[#18365E]" style={{ fontWeight: 600 }}>
                        {invitationData.name_first || invitationData.first_name}{' '}
                        {invitationData.name_last || invitationData.last_name}
                      </p>
                      <p className="text-xs text-[#717182]" style={{ fontWeight: 500 }}>
                        {invitationData.role || 'User'}
                      </p>
                    </div>
                  </div>
                  <div className="flex items-start gap-3">
                    <Mail className="w-5 h-5 text-[#18365E] mt-0.5" />
                    <div>
                      <p className="text-xs text-[#717182]" style={{ fontWeight: 500 }}>
                        Email
                      </p>
                      <p className="text-sm text-[#18365E]" style={{ fontWeight: 600 }}>
                        {email}
                      </p>
                    </div>
                  </div>
                </div>
              </div>

              <p className="text-sm text-[#717182] mb-6" style={{ fontWeight: 500 }}>
                You already have a password set. Please use the login page to access your account.
              </p>

              <Link
                to="/login"
                className="block w-full px-6 py-3 bg-gradient-to-r from-[#18365E] to-[#18365E]/90 text-white rounded-lg hover:shadow-lg transition-all text-center"
                style={{ fontWeight: 600 }}
              >
                Go to Login
              </Link>
            </div>
          </div>
        </div>
      </div>
    );
  }

  // Invitation validated - show welcome and password form (if needed)
  if (validationState === 'valid' && invitationData) {
    return (
      <div className="min-h-screen bg-gradient-to-br from-[#FAFAFA] via-white to-[#18365E]/5 flex items-center justify-center px-4 py-8">
        <div className="w-full max-w-md">
          {/* Logo/Brand Section */}
          <div className="text-center mb-8">
            <div className="inline-flex items-center justify-center mb-6">
              <img src={dvsLogo} alt="DVA Logo" className="h-24 w-auto" />
            </div>
            <h1
              className="text-3xl mb-2"
              style={{ fontWeight: 700, color: '#18365E', fontFamily: 'Montserrat, sans-serif' }}
            >
              Welcome to DVA!
            </h1>
            <p className="text-sm text-[#717182]" style={{ fontWeight: 500 }}>
              Your invitation has been verified. Set up your password to complete your account setup.
            </p>
          </div>

          {/* Welcome Card */}
          <div className="bg-white rounded-2xl shadow-xl border border-gray-100 p-8 mb-6">
            {/* User Details */}
            <div className="bg-gradient-to-br from-[#18365E]/5 to-[#FF6E4C]/5 rounded-lg p-4 mb-6">
              <div className="space-y-3">
                <div className="flex items-start gap-3">
                  <Building2 className="w-5 h-5 text-[#18365E] mt-0.5" />
                  <div>
                    <p className="text-xs text-[#717182]" style={{ fontWeight: 500 }}>
                      Organization
                    </p>
                    <p className="text-sm text-[#18365E]" style={{ fontWeight: 600 }}>
                      {invitationData.client_name || invitationData.client?.name || 'N/A'}
                    </p>
                  </div>
                </div>
                <div className="flex items-start gap-3">
                  <UserCircle className="w-5 h-5 text-[#18365E] mt-0.5" />
                  <div>
                    <p className="text-xs text-[#717182]" style={{ fontWeight: 500 }}>
                      Your Account
                    </p>
                    <p className="text-sm text-[#18365E]" style={{ fontWeight: 600 }}>
                      {invitationData.name_first || invitationData.first_name}{' '}
                      {invitationData.name_last || invitationData.last_name}
                    </p>
                    <p className="text-xs text-[#717182]" style={{ fontWeight: 500 }}>
                      {invitationData.role || 'User'}
                    </p>
                  </div>
                </div>
              </div>
            </div>

            {needsPassword ? (
              <>
                {/* Password Form */}
                {error && (
                  <div className="mb-6 p-4 bg-red-50 border border-red-200 rounded-lg flex items-start gap-3">
                    <AlertCircle className="w-5 h-5 text-red-500 flex-shrink-0 mt-0.5" />
                    <p className="text-sm text-red-700" style={{ fontWeight: 500 }}>
                      {error}
                    </p>
                  </div>
                )}

                <form onSubmit={handleSubmit} className="space-y-5">
                  <h3
                    className="text-lg mb-4"
                    style={{ fontWeight: 700, color: '#18365E', fontFamily: 'Montserrat, sans-serif' }}
                  >
                    Set Your Password
                  </h3>

                  {/* Email (read-only) */}
                  <div>
                    <label
                      htmlFor="email"
                      className="block text-sm mb-2 text-[#18365E]"
                      style={{ fontWeight: 600 }}
                    >
                      Email Address
                    </label>
                    <div className="relative">
                      <Mail className="absolute left-3 top-1/2 -translate-y-1/2 w-5 h-5 text-[#717182]" />
                      <input
                        id="email"
                        type="email"
                        value={email}
                        readOnly
                        className="w-full pl-11 pr-4 py-3 border border-gray-300 rounded-lg bg-gray-50 text-[#717182] cursor-not-allowed"
                        style={{ fontWeight: 500 }}
                      />
                    </div>
                  </div>

                  {/* Password */}
                  <div>
                    <label
                      htmlFor="password"
                      className="block text-sm mb-2 text-[#18365E]"
                      style={{ fontWeight: 600 }}
                    >
                      Password *
                    </label>
                    <div className="relative">
                      <Lock className="absolute left-3 top-1/2 -translate-y-1/2 w-5 h-5 text-[#717182]" />
                      <input
                        id="password"
                        type={showPassword ? 'text' : 'password'}
                        value={password}
                        onChange={(e) => setPassword(e.target.value)}
                        placeholder="Enter your password"
                        className="w-full pl-11 pr-12 py-3 border border-gray-300 rounded-lg focus:outline-none focus:ring-2 focus:ring-[#18365E] focus:border-transparent text-[#18365E] transition-all"
                        style={{ fontWeight: 500 }}
                        disabled={submitting}
                        required
                      />
                      <button
                        type="button"
                        onClick={() => setShowPassword(!showPassword)}
                        className="absolute right-3 top-1/2 -translate-y-1/2 text-[#717182] hover:text-[#18365E] transition-colors"
                        disabled={submitting}
                      >
                        {showPassword ? <EyeOff className="w-5 h-5" /> : <Eye className="w-5 h-5" />}
                      </button>
                    </div>
                    <p className="mt-1 text-xs text-[#717182]" style={{ fontWeight: 500 }}>
                      At least 8 characters with uppercase, lowercase, and numbers
                    </p>
                  </div>

                  {/* Confirm Password */}
                  <div>
                    <label
                      htmlFor="confirmPassword"
                      className="block text-sm mb-2 text-[#18365E]"
                      style={{ fontWeight: 600 }}
                    >
                      Confirm Password *
                    </label>
                    <div className="relative">
                      <Lock className="absolute left-3 top-1/2 -translate-y-1/2 w-5 h-5 text-[#717182]" />
                      <input
                        id="confirmPassword"
                        type={showConfirmPassword ? 'text' : 'password'}
                        value={confirmPassword}
                        onChange={(e) => setConfirmPassword(e.target.value)}
                        placeholder="Confirm your password"
                        className="w-full pl-11 pr-12 py-3 border border-gray-300 rounded-lg focus:outline-none focus:ring-2 focus:ring-[#18365E] focus:border-transparent text-[#18365E] transition-all"
                        style={{ fontWeight: 500 }}
                        disabled={submitting}
                        required
                      />
                      <button
                        type="button"
                        onClick={() => setShowConfirmPassword(!showConfirmPassword)}
                        className="absolute right-3 top-1/2 -translate-y-1/2 text-[#717182] hover:text-[#18365E] transition-colors"
                        disabled={submitting}
                      >
                        {showConfirmPassword ? <EyeOff className="w-5 h-5" /> : <Eye className="w-5 h-5" />}
                      </button>
                    </div>
                  </div>

                  {/* Submit Button */}
                  <button
                    type="submit"
                    disabled={submitting}
                    className="w-full flex items-center justify-center gap-2 px-6 py-3 bg-gradient-to-r from-[#18365E] to-[#18365E]/90 text-white rounded-lg hover:shadow-lg transition-all disabled:opacity-50 disabled:cursor-not-allowed"
                    style={{ fontWeight: 600 }}
                  >
                    {submitting ? (
                      <>
                        <Loader2 className="w-5 h-5 animate-spin" />
                        Setting up your account...
                      </>
                    ) : (
                      'Set Password & Continue'
                    )}
                  </button>
                </form>
              </>
            ) : (
              <div className="text-center">
                <p className="text-sm text-[#717182] mb-6" style={{ fontWeight: 500 }}>
                  A verification email has been sent to <strong>{email}</strong>. Please check your
                  inbox to complete your account setup.
                </p>

                <Link
                  to="/login"
                  className="block w-full px-6 py-3 bg-gradient-to-r from-[#18365E] to-[#18365E]/90 text-white rounded-lg hover:shadow-lg transition-all text-center"
                  style={{ fontWeight: 600 }}
                >
                  Go to Login
                </Link>
              </div>
            )}
          </div>
        </div>
      </div>
    );
  }

  // Default: Should not reach here
  return null;
}
